import React from 'react';
import clsx from 'clsx';
import { useTranslation } from 'react-i18next';
import { PiCheckLight } from 'react-icons/pi';

/**
 * THE multi-step wizard step indicator.
 *
 * The matter wizard and the drafting wizard each carried their own copy of this
 * rail — same markup, same 8px discs, same hidden-on-mobile labels, and the same
 * `border-gray-300 bg-white text-gray-400 dark:bg-gray-800` upcoming state that
 * predates the brand tokens. One rail now, on tokens:
 *
 *   complete  success disc, checkmark
 *   current   champagne — the accent, used at disc scale and nowhere wider
 *   upcoming  hairline border over the sunken surface, muted glyph
 *   connector `bg-hairline`, filled with success once the step behind it is done
 *
 * Labels are passed untranslated (the step tables are module constants) and run
 * through `t()` here, which is what both call sites already did.
 */

export interface WizardStepRailItem<K extends string = string> {
  /** Stable step id — matched against `currentStep`. */
  key: K;
  /** Untranslated label; translated by the rail. */
  label: string;
  /** Small glyph shown in the disc until the step is completed. */
  icon?: React.ReactNode;
}

export interface WizardStepRailProps<K extends string = string> {
  steps: WizardStepRailItem<K>[];
  /** Key of the step being shown. Anything before it reads as complete. */
  currentStep: K;
  /** Wrapper class — defaults to the standard `px-6 pt-4 pb-2` header band. */
  className?: string;
  /** Width of the rail itself, matched to the form's `maxWidth`. */
  maxWidth?: string;
  'data-tour-id'?: string;
}

function WizardStepRail<K extends string = string>({
  steps,
  currentStep,
  className,
  maxWidth = 'max-w-3xl',
  'data-tour-id': dataTourId,
}: Readonly<WizardStepRailProps<K>>) {
  const { t } = useTranslation();
  const currentIdx = steps.findIndex((s) => s.key === currentStep);

  return (
    <nav
      className={clsx('px-6 pt-4 pb-2 flex-shrink-0', className)}
      aria-label={t('Progress')}
      data-tour-id={dataTourId}
    >
      <ol className={clsx('flex items-center gap-2 mx-auto', maxWidth)}>
        {steps.map((step, idx) => {
          const isCompleted = idx < currentIdx;
          const isCurrent = idx === currentIdx;
          return (
            <li key={step.key} className="flex items-center gap-2 flex-1">
              <div className="flex items-center gap-2 min-w-0">
                <div
                  aria-current={isCurrent ? 'step' : undefined}
                  className={clsx(
                    'flex-shrink-0 flex items-center justify-center w-8 h-8 rounded-full border-2 transition-all duration-200',
                    isCompleted
                      ? 'border-success-500 bg-success-500 text-white'
                      : isCurrent
                        ? 'border-champagne bg-champagne/15 text-champagne'
                        : 'border-hairline bg-surface-sunken/70 text-fg-muted'
                  )}
                >
                  {isCompleted ? <PiCheckLight className="w-4 h-4" /> : step.icon}
                </div>
                <span
                  className={clsx(
                    'text-xs font-medium truncate hidden sm:block',
                    isCurrent
                      ? 'text-champagne'
                      : isCompleted
                        ? 'text-fg-body'
                        : 'text-fg-muted'
                  )}
                >
                  {t(step.label)}
                </span>
              </div>
              {idx < steps.length - 1 && (
                <div
                  className={clsx(
                    'flex-1 h-px transition-colors duration-200',
                    isCompleted ? 'bg-success-500' : 'bg-hairline'
                  )}
                />
              )}
            </li>
          );
        })}
      </ol>
    </nav>
  );
}

export default WizardStepRail;
